// 종사자 의견청취(818~821) 의견 데이터 공통 처리
//
// subInfo.의견목록  = [{ 발언자, 의견, 조치계획, 조치결과, 개선결과, 기타내용, 묶음 }]
//   - 개선결과 : "" | "진행중" | "완료" | "기타"   ("" 은 미입력)
//   - 묶음     : 같은 조치 결과를 공유하는 의견끼리 같은 값. 지금은 문서 안에서만 뜻이 있는 숫자 번호.
//                나중에 같은 사업장의 다른 문서와 묶게 되면 "g:" 로 시작하는 공유 ID(글자)를 쓴다.
//                그래서 묶음 값은 숫자로 가정하지 말고 normGroupId / nextGroupId 로만 다룬다.
// subInfo.특이사항없음 = true 이면 의견목록은 비어 있다 (조치율 집계에서 제외)
// subInfo.종사자의견 / 조치계획 / 조치결과 = 의견목록을 글로 합친 값.
//   목록 검색(LIKE)·엑셀·옛 화면 호환용이라 저장할 때마다 flattenOpinions 로 다시 만든다.

export const 개선결과목록 = ["진행중", "완료", "기타"];

export function newOpinion(partial = {}) {
  return { 발언자: "", 의견: "", 조치계획: "", 조치결과: "", 개선결과: "", 기타내용: "", 묶음: 0, ...partial };
}

// 문서를 넘어 공유하는 묶음 ID 인지 ("g:" 로 시작하는 글자). 아직 만들지는 않지만 읽어도 깨지지 않게 한다.
export function isSharedGroupId(v) {
  return typeof v === "string" && v.startsWith("g:");
}

// 묶음 값 정리: 공유 ID 는 그대로, 숫자는 양수만, 그 밖은 0(없음)
export function normGroupId(v) {
  if (isSharedGroupId(v)) return v;
  const n = Number(v);
  return n > 0 ? n : 0;
}

// 문서 안에서 아직 안 쓴 숫자 묶음 번호 (공유 ID 는 세지 않는다)
export function nextGroupId(list) {
  return list.reduce((max, o) => Math.max(max, isSharedGroupId(o.묶음) ? 0 : Number(o.묶음) || 0), 0) + 1;
}

// 묶음 값이 없는 의견(새로 추가한 줄)에 문서 안에서 아직 안 쓴 번호를 준다.
// 순번(i+1)을 그대로 쓰면 묶기·삭제를 거친 문서에서 기존 묶음 번호와 겹쳐 새 줄이 엉뚱한 묶음에 끼어들 수 있다.
export function fillGroupIds(list) {
  let next = nextGroupId(list) - 1;
  return list.map((o) => {
    const id = normGroupId(o.묶음);
    if (id) return o.묶음 === id ? o : { ...o, 묶음: id };
    return { ...o, 묶음: ++next };
  });
}

// 새 구조·옛 구조 어느 쪽이든 { 특이사항없음, 의견목록, legacy } 로 돌려준다.
// legacy = true 는 의견목록 없이 글상자 3개(종사자의견·조치계획·조치결과)만 있는 옛 문서.
export function normalizeOpinions(subInfo) {
  const s = subInfo || {};
  if (Array.isArray(s.의견목록)) {
    const 의견목록 = fillGroupIds(s.의견목록.map((o) => newOpinion({ ...o, 묶음: normGroupId(o.묶음) })));
    return { 특이사항없음: !!s.특이사항없음, 의견목록: s.특이사항없음 ? [] : 의견목록, legacy: false };
  }
  // 옛 문서: 글상자 3개를 의견 1건(발언자 없음)으로 본다. 종사자의견이 비어 있으면 의견 없음.
  if (!`${s.종사자의견 || ""}`.trim()) return { 특이사항없음: false, 의견목록: [], legacy: true };
  return {
    특이사항없음: false,
    의견목록: [newOpinion({ 의견: s.종사자의견, 조치계획: s.조치계획 || "", 조치결과: s.조치결과 || "", 묶음: 1 })],
    legacy: true,
  };
}

// 조치결과 + 개선결과를 한 줄 글로
export function resultText(o) {
  const parts = [];
  if (o.조치결과) parts.push(o.조치결과);
  if (o.개선결과) parts.push(o.개선결과 === "기타" && o.기타내용 ? `(기타: ${o.기타내용})` : `(${o.개선결과})`);
  return parts.join(" ");
}

// 의견목록 → 옛 글상자 3개 값. 목록·검색·엑셀이 이 값을 그대로 쓴다.
export function flattenOpinions(의견목록, 특이사항없음) {
  if (특이사항없음) return { 종사자의견: "특이사항 없음", 조치계획: "", 조치결과: "" };
  const list = (의견목록 || []).filter((o) => o && (o.의견 || o.조치계획 || o.조치결과));
  if (!list.length) return { 종사자의견: "", 조치계획: "", 조치결과: "" };

  // 발언자 없는 의견 1건은 옛 문서 그대로 (번호·머리줄을 붙이지 않아 손대지 않은 문서의 글이 바뀌지 않는다)
  if (list.length === 1 && !list[0].발언자) {
    return { 종사자의견: list[0].의견 || "", 조치계획: list[0].조치계획 || "", 조치결과: resultText(list[0]) };
  }

  const 의견줄 = [];
  const 계획줄 = [];
  const 결과줄 = [];
  list.forEach((o, i) => {
    const n = i + 1;
    if (o.발언자 && (i === 0 || list[i - 1].발언자 !== o.발언자)) 의견줄.push(`${i ? "\n" : ""}[${o.발언자}]`);
    의견줄.push(`${n}. ${o.의견 || ""}`);
    if (o.조치계획) 계획줄.push(`${n}. ${o.조치계획}`);
    const r = resultText(o);
    if (r) 결과줄.push(`${n}. ${r}`);
  });
  return { 종사자의견: 의견줄.join("\n"), 조치계획: 계획줄.join("\n"), 조치결과: 결과줄.join("\n") };
}

// subInfo 에 의견목록·특이사항없음과 합친 글을 함께 넣어 돌려준다 (원본은 바꾸지 않음)
export function applyOpinions(subInfo, 의견목록, 특이사항없음) {
  const list = 특이사항없음 ? [] : 의견목록 || [];
  return { ...(subInfo || {}), 의견목록: list, 특이사항없음: !!특이사항없음, ...flattenOpinions(list, 특이사항없음) };
}

// 산업안전보건위원회(818) 문서의 분기. 사용자가 입력하는 실시구분(header.quarter)이 기준이고,
// quarter 가 없는 옛 문서(2024년)는 header.month 로 계산한다.
export function getQuarter(header) {
  const quarter = Number(header && header.quarter);
  if (1 <= quarter && quarter <= 4) return quarter;
  const month = Number(header && header.month);
  return 1 <= month && month <= 12 ? Math.ceil(month / 3) : 0;
}

// ---------- 집계 (③ 실시현황, ⑤ 실시내역) ----------

// 개선결과별 건수. 미입력은 분모에만 들어간다.
export function opinionStats(의견목록) {
  const s = { n: 0, 완료: 0, 진행중: 0, 기타: 0, 미입력: 0 };
  for (const o of 의견목록 || []) {
    s.n++;
    if (o.개선결과 === "완료") s.완료++;
    else if (o.개선결과 === "진행중") s.진행중++;
    else if (o.개선결과 === "기타") s.기타++;
    else s.미입력++;
  }
  return s;
}

export function addStats(acc, s) {
  for (const k of ["n", "완료", "진행중", "기타", "미입력"]) acc[k] += s[k];
  return acc;
}

export function emptyStats() {
  return { n: 0, 완료: 0, 진행중: 0, 기타: 0, 미입력: 0 };
}

export function percent(a, b) {
  return b ? `${Math.round((a / b) * 100)}%` : "–";
}

// 조치율 = (완료 + 기타) ÷ 전체 의견 × 100. 특이사항 없음 회의는 의견이 없어 자연히 빠진다.
export function actionRate(stats) {
  return percent(stats.완료 + stats.기타, stats.n);
}

// 회의록 업로드(subInfo.회의록업로드, FileUploader 값) 가 있는지
export function hasMinutes(body) {
  const f = body && body.subInfo && body.subInfo.회의록업로드;
  return !!(f && (f.fileName || f.name || f.path || f.id));
}

// 회의자료(파일첨부 모듈, body.fileList) 가 있는지
export function hasMaterials(body) {
  return !!(body && Array.isArray(body.fileList) && body.fileList.length);
}

// ---------- 사업장 표시 순서 (③ 실시현황, ⑤ 실시내역) ----------
// 사업장 행은 이 순서대로, 목록에 없는 사업장은 그 뒤에 불러온 순서대로 둔다.
export const 사업장순서 = ["본사", "인천공장", "포항공장", "당진공장", "중앙기술연구소"];

export function sortSites(sites, labelOf = (s) => s.label) {
  const rank = (s) => {
    const i = 사업장순서.indexOf(`${labelOf(s) || ""}`.trim());
    return i < 0 ? 사업장순서.length : i;
  };
  return sites
    .map((s, i) => ({ s, i }))
    .sort((a, b) => rank(a.s) - rank(b.s) || a.i - b.i)
    .map((x) => x.s);
}
